/**
 * Observers slice for the application store.
 * Handles observer storage and retrieval.
 */

import type { Observer } from '@features/observers/types';
import type { StateCreator } from 'zustand';

/**
 * Observers state and actions.
 */
export interface ObserversSlice {
  // State
  observers: Observer[];
  isLoadingObservers: boolean;
  observersError: string | null;

  // Actions
  setObservers: (observers: Observer[]) => void;
  upsertObserver: (observer: Observer) => void;
  removeObserver: (id: number) => void;
  setIsLoadingObservers: (loading: boolean) => void;
  setObserversError: (error: string | null) => void;
}

/**
 * Creates the observers slice.
 */
export const createObserversSlice: StateCreator<ObserversSlice, [], [], ObserversSlice> = set => ({
  // Initial state
  observers: [],
  isLoadingObservers: false,
  observersError: null,

  // Actions
  setObservers: observers => set({ observers }),

  upsertObserver: observer =>
    set(state => {
      const idx = state.observers.findIndex(o => o.id === observer.id);
      if (idx === -1) return { observers: [observer, ...state.observers] };
      const next = state.observers.slice();
      next[idx] = observer;
      return { observers: next };
    }),

  removeObserver: id => set(state => ({ observers: state.observers.filter(o => o.id !== id) })),

  setIsLoadingObservers: loading => set({ isLoadingObservers: loading }),

  setObserversError: error => set({ observersError: error }),
});
